import { PlatformReportSortBy, useBaseAuthenticated, useReactRouterPagination } from 'shared/base';
import { usePlatformListReports } from 'shared/base/api/platform';
import { useReactRouterSearchTerm } from 'shared/base/hooks/useReactRouterSearchTerm';
import { useReactRouterSorting } from 'shared/base/hooks/useReactRouterSorting';

export function useReports() {
  const { currentMembership } = useBaseAuthenticated();
  const { apiSearchTerm, searchTerm, setSearchTerm } = useReactRouterSearchTerm();
  const {
    apiPage: publicApiPage,
    perPage: publicPerPage,
    setPerPage: setPublicPerPage,
    setTablePage: setPublicTablePage,
    tablePage: publicTablePage,
  } = useReactRouterPagination({
    pageParameter: 'public_page',
    perPageParameter: 'public_per_page',
    tableId: 'reports-public',
  });
  const {
    handleChangeSort: handlePublicChangeSort,
    sortBy: publicSortBy,
    sortOrder: publicSortOrder,
  } = useReactRouterSorting<PlatformReportSortBy>({
    defaultSortDirection: 'asc',
    defaultSortValue: 'title',
    tableId: 'reports-public',
  });
  const {
    apiPage: privateApiPage,
    perPage: privatePerPage,
    setPerPage: setPrivatePerPage,
    setTablePage: setPrivateTablePage,
    tablePage: privateTablePage,
  } = useReactRouterPagination({
    tableId: 'reports-private',
  });
  const {
    handleChangeSort: handlePrivateChangeSort,
    sortBy: privateSortBy,
    sortOrder: privateSortOrder,
  } = useReactRouterSorting<PlatformReportSortBy>({
    defaultSortDirection: 'asc',
    defaultSortValue: 'title',
    tableId: 'reports-private',
  });

  const {
    data: publicReportsData,
    isError: publicReportsIsError,
    isLoading: publicReportsIsLoading,
  } = usePlatformListReports(currentMembership.team.name, {
    page: publicApiPage,
    per_page: publicPerPage,
    visibility: 'public',
    ...(publicSortBy
      ? {
          sort_by: [publicSortBy],
          sort_dir: [publicSortOrder],
        }
      : {}),
    ...(apiSearchTerm ? { search_term: apiSearchTerm } : {}),
  });

  const {
    data: privateReportsData,
    isError: privateReportsIsError,
    isLoading: privateReportsIsLoading,
  } = usePlatformListReports(currentMembership.team.name, {
    page: privateApiPage,
    per_page: privatePerPage,
    visibility: 'private',
    ...(privateSortBy
      ? {
          sort_by: [privateSortBy],
          sort_dir: [privateSortOrder],
        }
      : {}),
    ...(apiSearchTerm ? { search_term: apiSearchTerm } : {}),
  });

  const hasPrivateReports =
    !privateReportsIsLoading && (privateReportsData?.data?.metadata?.total_count ?? 0) > 0;

  const hasPublicReports =
    !publicReportsIsLoading && (publicReportsData?.data?.metadata?.total_count ?? 0) > 0;

  return {
    handlePrivateChangeSort,
    handlePublicChangeSort,
    hasPrivateReports,
    hasPublicReports,
    privatePerPage,
    privateReportsData,
    privateReportsIsError,
    privateReportsIsLoading,
    privateSortBy,
    privateSortOrder,
    privateTablePage,
    publicApiPage,
    publicPerPage,
    publicReportsData,
    publicReportsIsError,
    publicReportsIsLoading,
    publicSortBy,
    publicSortOrder,
    publicTablePage,
    searchTerm,
    setPrivatePerPage,
    setPrivateTablePage,
    setPublicPerPage,
    setPublicTablePage,
    setSearchTerm,
  };
}
